/**
 * Layout Component
 *
 * Role-based navigation per VISION.md:
 * - Trainers see clean UI without technical jargon
 * - Admins see full system admin tools
 */

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';

const operatorNavItems = [
  { to: '/console', label: 'Dashboard', icon: 'grid-2x2' },
  { to: '/console/assessments', label: 'Assessments', icon: 'clipboard-list' },
  { to: '/console/artifacts', label: 'Artifacts', icon: 'file-text' },
  { to: '/console/rubrics', label: 'Rubrics', icon: 'scale' },
  { to: '/console/scores', label: 'Scores', icon: 'bar-chart-2' },
  { to: '/console/subscriptions', label: 'Subscriptions', icon: 'webhook' },
  { to: '/console/deliveries', label: 'Deliveries', icon: 'send' },
  { to: '/console/exports', label: 'Exports', icon: 'download' },
];

const trainerNavItems = [
  { to: '/console/trainer', label: 'Dashboard', icon: 'layout-dashboard' },
  { to: '/console/trainer/rubrics', label: 'Rubrics', icon: 'scale' },
  { to: '/console/trainer/assessments', label: 'Assessments', icon: 'clipboard-check' },
];

export function Layout() {
  const { user, role, signOut, company } = useAuth();
  const navigate = useNavigate();

  // Role-based visibility
  const isAdmin = role === 'super_admin' || role === 'company_admin';
  const isTrainer = role === 'trainer';

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navLinkStyles = (isActive: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px',
    margin: '2px 8px',
    borderRadius: 8,
    color: isActive ? 'var(--sky-600)' : 'var(--slate-600)',
    textDecoration: 'none',
    background: isActive ? 'var(--sky-50)' : 'transparent',
    fontSize: 14,
    fontWeight: isActive ? 500 : 400,
    transition: 'all 0.15s ease',
    borderLeft: isActive ? '2px solid var(--sky-500)' : '2px solid transparent',
  });

  // User display name
  const displayName = user?.name || user?.email?.split('@')[0] || 'User';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--slate-50)' }}>
      {/* Sidebar */}
      <nav
        style={{
          width: 220,
          background: 'white',
          borderRight: '1px solid var(--slate-200)',
          padding: '20px 0',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Brand */}
        <div
          style={{
            padding: '0 20px 24px',
            borderBottom: '1px solid var(--slate-100)',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'var(--slate-900)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <iconify-icon icon="lucide:activity" width="18" height="18"></iconify-icon>
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--slate-900)', letterSpacing: '-0.02em' }}>
              Saqr Skills
            </span>
          </div>
        </div>

        {/* System Admin Section - Only for admins */}
        {isAdmin && (
          <>
            <div
              style={{
                padding: '8px 20px 6px',
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--slate-400)',
              }}
              title="Internal system administration and debugging tools"
            >
              System Admin
            </div>
            {operatorNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/console'}
                style={({ isActive }) => navLinkStyles(isActive)}
              >
                <iconify-icon
                  icon={`lucide:${item.icon}`}
                  width="16"
                  height="16"
                  style={{ opacity: 0.7 }}
                ></iconify-icon>
                {item.label}
              </NavLink>
            ))}
          </>
        )}

        {/* Trainer Section - Always visible */}
        <div
          style={{
            padding: isAdmin ? '20px 20px 6px' : '8px 20px 6px',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--slate-400)',
            borderTop: isAdmin ? '1px solid var(--slate-100)' : 'none',
            marginTop: isAdmin ? 12 : 0,
          }}
          title="Assessment authoring: create rubrics and assessments for learners"
        >
          {isTrainer ? 'My Workspace' : 'Trainer'}
        </div>
        {trainerNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/console/trainer'}
            style={({ isActive }) => navLinkStyles(isActive)}
          >
            <iconify-icon
              icon={`lucide:${item.icon}`}
              width="16"
              height="16"
              style={{ opacity: 0.7 }}
            ></iconify-icon>
            {item.label}
          </NavLink>
        ))}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* User info and sign out */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--slate-100)' }}>
          {/* User badge */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              marginBottom: 12,
              background: 'var(--slate-50)',
              borderRadius: 10,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'var(--sky-100)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--sky-600)',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--slate-900)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </div>
              {company && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--slate-500)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {company.name}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSignOut}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '10px 16px',
              borderRadius: 9999,
              background: 'var(--slate-100)',
              border: 'none',
              color: 'var(--slate-600)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--slate-200)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'var(--slate-100)';
            }}
          >
            <iconify-icon icon="lucide:log-out" width="14" height="14"></iconify-icon>
            Sign Out
          </button>
        </div>
      </nav>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Page content */}
        <main style={{ flex: 1, padding: 24, overflow: 'auto', background: 'var(--slate-50)' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
